import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MessageSquare, Lightbulb, Hammer, CheckCircle, TrendingUp, CircleDot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const fadeUp = {
 initial: { opacity: 0, y: 30 },
 animate: { opacity: 1, y: 0 },
 transition: { duration: 0.6 },
};

const processSteps = [
 {
  number: "01",
  icon: MessageSquare,
  title: "Submit Your Wishlist",
  description: "Tell us what you're thinking about building. No commitment. just a starting point for conversation.",
  details: [
   "Share your idea and context",
   "We review within 48 hours",
   "Honest assessment of fit",
  ],
 },
 {
  number: "02",
  icon: Lightbulb,
  title: "Validate Demand",
  description: "We test your assumptions with real customers. Customer interviews, demand testing, pricing validation.",
  details: [
   "Customer conversations",
   "Demand validation",
   "Pricing strategy",
  ],
  timeline: "Week 1-2",
 },
 {
  number: "03",
  icon: Hammer,
  title: "We Build Your Product",
  description: "We build your MVP. not advise on it. Real product, real users, iterating on real feedback.",
  details: [
   "Full product development",
   "User testing and iteration",
   "You focus on vision",
  ],
  timeline: "Week 3-6",
 },
 {
  number: "04",
  icon: TrendingUp,
  title: "We Get Your First Customers",
  description: "Marketing and sales to acquire your first paying customers. Real revenue, real traction.",
  details: [
   "Go-to-market execution",
   "Sales and marketing",
   "First paying customers",
  ],
  timeline: "Post-Launch",
 },
 {
  number: "05",
  icon: CheckCircle,
  title: "Handover or Scale Together",
  description: "Your product is seed-funding ready with paying customers. Take it forward, or continue with us to scale.",
  details: [
   "Seed-funding ready product",
   "Documented handover",
   "Optional scaling support",
  ],
  timeline: "Your Choice",
 },
];

const processPrinciples = [
 "We build. not just advise",
 "Paying customers before scaling",
 "Seed-funding ready at handover",
 "Your choice: take over or scale with us",
];

// Person climbing stairs illustration - step by step journey
const illustration = "/images/process.png";

export default function ProcessPage() {
 return (
  <div className="flex flex-col">
   {/* Hero */}
   <section className="bg-hero py-16 sm:py-20 lg:py-24">
    <div className="container-narrow">
     <motion.div
      initial="initial"
      animate="animate"
      variants={fadeUp}
      className="text-center"
     >
      <h1 className="heading-display text-foreground mb-6">
       Our Process
      </h1>
      <p className="body-large text-muted-foreground max-w-2xl mx-auto">
       We validate, build, and get your first paying customers. then you decide: take over or scale with us.
      </p>
     </motion.div>
    </div>
   </section>

   {/* Process Visual */}
   <section className="bg-background py-12 sm:py-16">
    <div className="container-wide">
     <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      {/* Illustration - Person climbing stairs */}
      <motion.div
       initial={{ opacity: 0, x: -30 }}
       whileInView={{ opacity: 1, x: 0 }}
       viewport={{ once: true, margin: "-100px" }}
       transition={{ duration: 0.7 }}
       className="order-2 lg:order-1 flex justify-center"
      >
       <div className="w-full max-w-xs">
        <img
         src={illustration}
         alt="Step by step journey to clarity"
         className="w-full h-auto"
        />
       </div>
      </motion.div>

      {/* Summary */}
      <motion.div
       initial={{ opacity: 0, x: 30 }}
       whileInView={{ opacity: 1, x: 0 }}
       viewport={{ once: true, margin: "-100px" }}
       transition={{ duration: 0.7, delay: 0.1 }}
       className="order-1 lg:order-2"
      >
       <h2 className="heading-large text-foreground mb-5">A Path to Clarity</h2>
       <p className="body-base text-muted-foreground mb-6">
        We do not believe in long engagements with uncertain outcomes. Our process is designed for speed and learning. short cycles that give you real answers, fast.
       </p>
       
       <div className="space-y-3">
        {processPrinciples.map((principle, i) => (
         <motion.div
          key={i}
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 + i * 0.1 }}
          className="flex items-center gap-3"
         >
          <div className="w-2 h-2 rounded-full bg-accent flex-shrink-0" />
          <span className="text-foreground font-medium">{principle}</span>
         </motion.div>
        ))}
       </div>
      </motion.div>
     </div>
    </div>
   </section>

   {/* Process Steps */}
   <section className="bg-section py-16 sm:py-20 lg:py-24">
    <div className="container-base">
     <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6 }}
      className="text-center mb-12"
     >
      <h2 className="heading-large text-foreground mb-5">The Five Steps</h2>
      <p className="body-base text-muted-foreground max-w-2xl mx-auto">
       Each step is designed to give you clarity and confidence for the next decision.
      </p>
     </motion.div>

     <div className="relative">
      {/* Vertical line */}
      <div className="hidden md:block absolute left-8 top-0 bottom-0 w-px bg-border" />
      
      <div className="space-y-6">
       {processSteps.map((step, i) => (
        <motion.div
         key={step.number}
         initial={{ opacity: 0, x: -30 }}
         whileInView={{ opacity: 1, x: 0 }}
         viewport={{ once: true }}
         transition={{ duration: 0.6, delay: i * 0.1 }}
         className="relative"
        >
         <Card className="card-elevated md:ml-16">
          <CardContent className="p-6 lg:p-8">
           {/* Number badge - positioned on line */}
           <div className="hidden md:flex absolute -left-16 top-8 w-16 items-center justify-center">
            <motion.div 
             className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-accent-foreground font-medium text-sm z-10"
             initial={{ scale: 0 }}
             whileInView={{ scale: 1 }}
             viewport={{ once: true }}
             transition={{ delay: 0.2 + i * 0.1, type: "spring", stiffness: 200 }}
            >
             {step.number}
            </motion.div>
           </div>
           
           <div className="flex flex-col lg:flex-row lg:items-start gap-6">
            <div className="flex items-center gap-4 lg:hidden">
             <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-accent-foreground font-medium text-sm">
              {step.number}
             </div>
             <h3 className="font-serif text-xl font-medium text-foreground">{step.title}</h3>
            </div>
            
            <div className="hidden lg:block">
             <div className="icon-container">
              <step.icon className="h-6 w-6 text-accent" />
             </div>
            </div>
            
            <div className="flex-1">
             <h3 className="hidden lg:block font-serif text-xl font-medium text-foreground mb-2">{step.title}</h3>
             <p className="text-muted-foreground mb-4">{step.description}</p>
             <ul className="flex flex-wrap gap-2">
              {step.details.map((detail, j) => (
               <li key={j} className="flex items-center gap-2 text-sm text-foreground bg-muted/50 px-3 py-1.5 rounded-full">
                <CircleDot className="h-3 w-3 text-accent" />
                {detail}
               </li>
              ))}
             </ul>
            </div>
           </div>
          </CardContent>
         </Card>
        </motion.div>
       ))}
      </div>
     </div>
    </div>
   </section>

   {/* CTA */}
   <section className="bg-primary text-primary-foreground py-16 sm:py-20">
    <div className="container-narrow text-center">
     <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
     >
      <h2 className="heading-section mb-5">Ready to Start?</h2>
      <p className="body-base text-primary-foreground/80 mb-6">
       The first step is simple: tell us what you are thinking about.
      </p>
      <div className="flex flex-col sm:flex-row justify-center gap-4">
       <Button variant="secondary" size="lg" asChild className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 rounded-full">
        <Link to="/contact">
         Submit Your Wishlist
         <ArrowRight className="h-4 w-4 ml-2" />
        </Link>
       </Button>
       <Button variant="outline" size="lg" asChild className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 rounded-full">
        <Link to="/pricing">See Pricing</Link>
       </Button>
      </div>
     </motion.div>
    </div>
   </section>
  </div>
 );
}
